import React, { useCallback, useEffect, useRef, useState } from "react";
import createGlobe from "cobe";
import { Box, useStyleConfig } from "@chakra-ui/react";

// Default Globe Configuration (can be overridden by props)
const GLOBE_CONFIG = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 3.5, // Start viewing Asia/Pacific region
  theta: 0.3,
  dark: 1, // Using 1 for dark theme to work well on black background
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1], // White base color
  markerColor: [251 / 255, 100 / 255, 21 / 255], // Orange marker color
  glowColor: [1, 1, 1], // White glow
  markers: [
    // Default markers (can be overridden)
    { location: [14.5995, 120.9842], size: 0.03 }, // Manila
    { location: [19.076, 72.8777], size: 0.1 }, // Mumbai
    { location: [23.8103, 90.4125], size: 0.05 }, // Dhaka
    { location: [30.0444, 31.2357], size: 0.07 }, // Cairo
    { location: [39.9042, 116.4074], size: 0.08 }, // Beijing
    { location: [-23.5505, -46.6333], size: 0.1 }, // São Paulo
    { location: [19.4326, -99.1332], size: 0.1 }, // Mexico City
    { location: [34.6937, 135.5022], size: 0.05 }, // Osaka
    { location: [41.0082, 28.9784], size: 0.06 }, // Istanbul
  ],
};

export function Globe({ config = GLOBE_CONFIG, sx, ...props }) {
  let phi = config.phi || 3.5; // Initialize with config phi value or default to Asia/Pacific
  let width = 0;
  const canvasRef = useRef(null);
  const pointerInteracting = useRef(null);
  const pointerInteractionMovement = useRef(0);
  const [r, setR] = useState(0);

  // Determine globe theme based on Chakra UI color mode (optional, defaults to light)
  // const { colorMode } = useColorMode();
  // const isDark = colorMode === 'dark';
  // config.dark = isDark ? 1 : 0;
  // config.glowColor = isDark ? [0.8, 0.8, 0.8] : [1, 1, 1]; // Adjust glow for dark mode if needed
  // config.baseColor = isDark ? [0.1, 0.1, 0.1] : [1, 1, 1]; // Adjust base for dark mode if needed

  const updatePointerInteraction = (value) => {
    pointerInteracting.current = value;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = value ? "grabbing" : "grab";
    }
  };

  const updateMovement = (clientX) => {
    if (pointerInteracting.current !== null) {
      const delta = clientX - pointerInteracting.current;
      pointerInteractionMovement.current = delta;
      setR(delta / 200); // Rotation speed based on drag distance
    }
  };

  const onRender = useCallback(
    (state) => {
      // Called on every animation frame. Use it to modify state.
      if (!pointerInteracting.current) phi += 0.005; // Auto-rotate
      state.phi = phi + r; // Apply rotation from interaction + auto-rotate
      state.width = width * 2; // Adjust size based on container
      state.height = width * 2;
    },
    [r] // Depend on r to update rotation when interaction changes
  );

  const onResize = () => {
    if (canvasRef.current) {
      width = canvasRef.current.offsetWidth;
    }
  };

  useEffect(() => {
    window.addEventListener("resize", onResize);
    onResize(); // Initial size calculation

    const globe = createGlobe(canvasRef.current, {
      ...config, // Use provided or default config
      width: width * 2,
      height: width * 2,
      onRender, // Pass the render callback
    });

    // Fade in animation
    setTimeout(() => {
      if (canvasRef.current) {
        canvasRef.current.style.opacity = "1";
      }
    }, 500); // Start fade in slightly after mount

    // Cleanup function
    return () => {
      globe.destroy();
      window.removeEventListener("resize", onResize);
    };
  }, [config, onRender]); // Re-create globe if config or onRender changes

  return (
    // Main container Box - Remove absolute positioning, let parent control layout
    <Box
      // position="absolute" // Removed
      // inset="0" // Removed
      // mx="auto" // Removed (parent Center handles centering)
      aspectRatio={1 / 1} // Maintain aspect ratio
      w="full" // Fill the width provided by the parent
      h="full" // Fill the height provided by the parent
      sx={sx}
      {...props}
    >
      {/* Canvas element wrapped in a Box */}
      <Box
        as="canvas"
        ref={canvasRef}
        w="full" // Make canvas fill the container Box
        h="full"
        opacity={0} // Start transparent for fade-in effect
        transition="opacity 0.5s ease-in-out" // Smooth fade-in
        sx={{ contain: "layout paint size" }} // Performance optimization hint
        onPointerDown={(e) =>
          updatePointerInteraction(
            e.clientX - pointerInteractionMovement.current
          )
        }
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) =>
          e.touches[0] && updateMovement(e.touches[0].clientX)
        }
      />
    </Box>
  );
}
